import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DateRangePicker.css';

const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatRangeDate = (value) => {
  const date = fromDateKey(value);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const getCalendarDays = (viewDate) => {
  const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
  const firstCell = new Date(firstOfMonth);
  firstCell.setDate(firstCell.getDate() - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstCell);
    date.setDate(firstCell.getDate() + index);
    return date;
  });
};

const DateRangePicker = ({ id = 'date-range', value, onChange, legacyMonth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => fromDateKey(value.start) || new Date());
  const pickerRef = useRef(null);
  const calendarDays = useMemo(() => getCalendarDays(viewDate), [viewDate]);
  const todayKey = toDateKey(new Date());

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectRange = (startDate, endDate = startDate) => {
    onChange({ start: toDateKey(startDate), end: toDateKey(endDate) });
    setViewDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  };

  const selectPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (preset === 'today') {
      selectRange(today);
    } else if (preset === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      selectRange(yesterday);
    } else {
      const start = new Date(today);
      start.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      selectRange(start, end);
    }
    setIsOpen(false);
  };

  const selectDay = (date) => {
    const key = toDateKey(date);
    if (!value.start || value.end) {
      onChange({ start: key, end: '' });
      return;
    }

    onChange(key < value.start
      ? { start: key, end: value.start }
      : { start: value.start, end: key });
  };

  const selectWholeMonth = () => {
    const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const end = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    selectRange(start, end);
    setIsOpen(false);
  };

  const triggerText = value.start
    ? (value.end && value.end !== value.start
      ? `${formatRangeDate(value.start)} – ${formatRangeDate(value.end)}`
      : formatRangeDate(value.start))
    : (legacyMonth ? `${legacyMonth} (all years)` : 'All dates');

  return (
    <div className="events-date-picker" ref={pickerRef}>
      <button
        id={id}
        className={`events-date-trigger ${value.start || legacyMonth ? 'has-value' : ''}`}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="events-date-trigger-icon" aria-hidden="true">▦</span>
        <span>{triggerText}</span>
        <span className="events-date-trigger-chevron" aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div className="events-calendar-popover" role="dialog" aria-label="Choose a date range">
          <div className="events-calendar-presets" aria-label="Quick date ranges">
            <button type="button" onClick={() => selectPreset('today')}>Today</button>
            <button type="button" onClick={() => selectPreset('yesterday')}>Yesterday</button>
            <button type="button" onClick={() => selectPreset('week')}>Current Week</button>
          </div>

          <div className="events-calendar-header">
            <button type="button" className="events-calendar-nav" aria-label="Previous month" onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}>‹</button>
            <strong>{viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
            <button type="button" className="events-calendar-nav" aria-label="Next month" onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}>›</button>
          </div>

          <button type="button" className="events-calendar-month-action" onClick={selectWholeMonth}>Select entire month</button>

          <div className="events-calendar-grid">
            {CALENDAR_WEEKDAYS.map((day) => <span key={day} className="events-calendar-weekday">{day}</span>)}
            {calendarDays.map((date) => {
              const key = toDateKey(date);
              const isOutside = date.getMonth() !== viewDate.getMonth();
              const isEndpoint = key === value.start || key === value.end;
              const isInRange = value.start && value.end && key > value.start && key < value.end;
              return (
                <button
                  key={key}
                  type="button"
                  className={`events-calendar-day${isOutside ? ' outside' : ''}${isInRange ? ' in-range' : ''}${isEndpoint ? ' endpoint' : ''}${key === todayKey ? ' today' : ''}`}
                  aria-label={date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                  onClick={() => selectDay(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="events-calendar-footer">
            <span>{value.start && !value.end ? 'Now choose an end date' : 'Select any two dates for a range'}</span>
            <button type="button" onClick={() => { onChange({ start: '', end: '' }); setIsOpen(false); }}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
